import { useMemo } from 'react';

import { useSignalsSearchParams } from '@/pages/workspace/signals/search-params-schema';
import { getTimezoneDayjs } from '@/utils/time';

import {
  normalizeSignalPresetForQuery,
} from '../lib';
import type { SIGNAL_PERIOD, SignalDateSelectionInput } from '../model';
import { useComputedIssues } from './use-computed-issues';
import { useGlobalSignalIssueState } from './use-global-signal-issue-state';
import { useIssueListQuery } from './use-issue-list-query';

export const buildComputedIssueListNormalizedRange = (
  dateSelection: SignalDateSelectionInput,
) => {
  return normalizeSignalPresetForQuery(dateSelection);
};

export const buildComputedIssueListQueryInput = (params: {
  datePreset: SignalDateSelectionInput;
  compositedFilter: StructuredFilter;
}): { filterParams: StructuredFilter; period: SIGNAL_PERIOD } => {
  const normalized = buildComputedIssueListNormalizedRange(params.datePreset);

  return {
    filterParams: {
      ...params.compositedFilter,
      start_date: normalized.startDate,
      end_date: normalized.endDate,
    },
    period: normalized.period,
  };
};

export const useComputedIssueList = () => {
  const { dateSelection, compositedFilter } = useGlobalSignalIssueState();
  const [params] = useSignalsSearchParams();

  const { sortBy, sortOrder } = params;
  const { filterParams, period } = buildComputedIssueListQueryInput({
    datePreset: dateSelection,
    compositedFilter,
  });

  const {
    originIssues,
    issueAggregates,
    signals,
    aggregateFetching,
    originIssueFetching,
    signalsFetching,
  } = useIssueListQuery({
    filterParams,
    period,
  });

  const isLoading = aggregateFetching || originIssueFetching || signalsFetching;

  const computedIssues = useComputedIssues({
    issueAggregates,
    originIssues,
    signals,
    datePreset: dateSelection,
    sortStatus: { sortBy, orderStatus: sortOrder },
  });

  const lastUpdatedAt = useMemo(() => {
    if (originIssues.length === 0) return null;
    return originIssues.reduce(
      (acc, value) => {
        const detectedAt = getTimezoneDayjs(value.detected_at);
        if (detectedAt.isAfter(acc)) {
          return detectedAt;
        }
        return acc;
      },
      getTimezoneDayjs().addVal(-3, 'year'),
    );
  }, [originIssues]);

  return {
    computedIssues,
    isLoading,
    lastUpdatedAt,
  };
};
